import { FC, PropsWithChildren } from 'react';
import { cx } from '@emotion/css';
import { Trans, useTranslation } from 'next-i18next';

import { PhoenixIcon } from '@phoenix/design-system/frontend/ui/phoenix-icon/phoenix-icon';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { LinkOut } from '@phoenix/core/frontend/shared/ui/link-out/link-out';

type ToolShedHintLink = {
  name: string;
  url: string;
  isInline?: boolean;
  isInternal?: boolean;
  allowReferrer?: boolean;
};

interface ToolShedHintProps {
  hint: string;
  link?: ToolShedHintLink;
  className?: string;
}

const LinkOutWrapperContainer = ({ children, block }) => (block ? <span className="block mt-1">{children}</span> : children);

const LinkOutWrapper: FC<PropsWithChildren<{ link: ToolShedHintLink; block?: boolean }>> = ({ link, children, block = false }) => {
  const { t } = useTranslation('shared');

  return (
    <LinkOutWrapperContainer block={block}>
      <LinkOut href={link.url} allowReferrer={link.allowReferrer} dataTestId={link.name}>
        {children}
        <span className="sr-only">{t('shared_a11y_hint_opensInNewTab')}</span>
      </LinkOut>
    </LinkOutWrapperContainer>
  );
};

export const ToolShedHint: FC<ToolShedHintProps> = ({ className, hint, link }) => (
  <div
    aria-live="polite"
    className={cx(className, 'card flex flex-col gap-x-2 gap-y-4 p-6 stihl-text-body-default-reg miniTablet:flex-row')}
  >
    <PhoenixIcon className="mt-1 h-4 max-w-[16px]" icon={getDesignSystemIcon('icon-info-16.svg')} />
    <p className="max-w-[600px] whitespace-pre-line">
      <Trans
        i18nKey={hint}
        components={{
          ...(link && {
            [link.name]: <LinkOutWrapper link={link} block={!link.isInline} />,
          }),
        }}
      />
    </p>
  </div>
);
